import { useEffect, useRef, useState, useMemo } from 'react';

interface LazyIframeProps {
  src: string;
  title: string;
  width?: string | number;
  height?: string | number;
  className?: string;
  style?: React.CSSProperties;
  allow?: string;
  frameBorder?: string;
}

export default function LazyIframe({
  src,
  title,
  width = '100%',
  height = 500,
  className = '',
  style = {},
  allow = '',
  frameBorder = '0',
}: LazyIframeProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Memoize container styles to ensure consistent values between server and client
  // Convert all values to strings with explicit units to avoid hydration mismatches
  const containerStyle = useMemo<React.CSSProperties>(() => {
    const heightStr = typeof height === 'number' ? `${height}px` : (String(height).match(/^\d+$/) ? `${height}px` : height);
    const widthStr = typeof width === 'number' ? `${width}px` : (String(width).match(/^\d+$/) ? `${width}px` : width);
    
    return {
      width: widthStr,
      height: heightStr,
      minHeight: heightStr,
      ...style,
    };
  }, [height, width, style]);

  // Use Intersection Observer to lazy load iframe when in viewport
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: '200px', // Start loading 200px before it enters viewport
        threshold: 0,
      }
    );

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={containerRef}
      className={`relative ${className}`}
      style={containerStyle}
    >
      {!shouldLoad ? (
        // Placeholder skeleton
        <div className="absolute inset-0 bg-cyber-darker animate-pulse rounded-xl flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-12 h-12 border-4 border-cyber-cyan/30 border-t-cyber-cyan rounded-full animate-spin" />
            <span className="text-white/40 text-sm font-rajdhani">Loading...</span>
          </div>
        </div>
      ) : (
        <>
          {/* Loading state while iframe loads */}
          {!isLoaded && (
            <div className="absolute inset-0 bg-cyber-darker animate-pulse rounded-xl flex items-center justify-center z-10">
              <div className="flex flex-col items-center gap-3">
                <div className="w-12 h-12 border-4 border-cyber-cyan/30 border-t-cyber-cyan rounded-full animate-spin" />
                <span className="text-white/40 text-sm font-rajdhani">Loading player...</span>
              </div>
            </div>
          )}
          <iframe
            src={src}
            title={title}
            width="100%"
            height="100%"
            frameBorder={frameBorder}
            allow={allow || undefined}
            loading="lazy"
            onLoad={() => setIsLoaded(true)}
            className={`absolute inset-0 w-full h-full transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
            style={{ borderRadius: '12px' }}
          />
        </>
      )}
    </div>
  );
}

